'use client';

import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { Suspense, ChangeEvent } from 'react';
import { LANGUAGE_TO_TENANT } from '@/lib/tenant';

const TENANT_OPTIONS = Object.entries(LANGUAGE_TO_TENANT).map(([language, tenant]) => ({
  label: language.charAt(0).toUpperCase() + language.slice(1),
  value: tenant,
}));

// Remove duplicates if multiple languages map to the same tenant (like odia and oriya)
const UNIQUE_TENANT_OPTIONS = Array.from(
  new Map(TENANT_OPTIONS.map((item) => [item.value, item])).values()
).sort((a, b) => a.label.localeCompare(b.label));

function TenantSelectorInner({ defaultTenant = 'en' }: { defaultTenant?: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentTenant = searchParams.get('lang') || defaultTenant;

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newTenant = e.target.value;
    const params = new URLSearchParams(searchParams.toString());
    
    if (newTenant === 'en') {
      params.delete('lang'); // keep URL clean for default
    } else {
      params.set('lang', newTenant);
    }

    const query = params.toString();
    router.push(`${pathname}${query ? `?${query}` : ''}`);
  };

  return (
    <div className='flex items-center gap-2'>
      <select
        id='tenant-select'
        value={currentTenant}
        onChange={handleChange}
        className='rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
      >
        {UNIQUE_TENANT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function TenantSelector(props: { defaultTenant?: string }) {
  return (
    <Suspense fallback={<div className="h-8 w-32 animate-pulse rounded-md bg-gray-200"></div>}>
      <TenantSelectorInner {...props} />
    </Suspense>
  );
}
